import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store, Loader2, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(identifier.trim(), password);
      nav("/", { replace: true });
    } catch (err) {
      setError(err.notAdmin ? err.message : fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-6 sm:p-10">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
          <div className="flex items-center gap-2 mb-8">
            <div className="h-11 w-11 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-extrabold text-lg font-outfit">S</div>
            <div>
              <div className="font-extrabold font-outfit text-lg leading-tight">Sonu Ki Dukaan</div>
              <div className="text-[11px] text-muted-foreground font-mono">Admin Panel</div>
            </div>
          </div>
          <h1 className="text-2xl font-extrabold font-outfit tracking-tight">Welcome back</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Sign in with your admin account to continue.</p>

          {error && (
            <div className="mb-4 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="identifier">Email or Phone</Label>
              <Input id="identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                placeholder="admin@sonu.com" autoComplete="username" required className="mt-1.5" data-testid="login-identifier-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" autoComplete="current-password" required className="mt-1.5" data-testid="login-password-input" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="admin-login-submit-button">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in…</> : "Sign in"}
            </Button>
            <a href="/forgot-password" className="block text-center text-sm font-medium text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</a>
          </div>
        </form>
      </div>
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-10 relative overflow-hidden">
        <div className="relative z-10">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-white/15">
            <MapPin className="h-3 w-3" /> Operating in Lucknow, UP
          </span>
        </div>
        <div className="relative z-10">
          <Store className="h-10 w-10 mb-4 opacity-90" />
          <h2 className="text-3xl font-extrabold font-outfit leading-tight">Bachat Ki Dukaan</h2>
          <p className="mt-3 text-sm text-primary-foreground/80 max-w-md">
            Manage shops, orders, delivery, coupons, referrals, loyalty and marketplace settings — all from one hyperlocal control center.
          </p>
        </div>
        <div className="absolute -right-20 -bottom-20 h-80 w-80 rounded-full bg-white/10" />
        <div className="absolute right-24 top-10 h-40 w-40 rounded-full bg-white/5" />
      </div>
    </div>
  );
}
